import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import axios from 'axios';
import { Clock, UserCheck, Calendar, Coffee, LogOut, Users, AlertCircle, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ShiftManagement from './ShiftManagement';import PageContainer from '../layout/PageContainer';


interface AttendanceRecord {
  id: number;
  staff_id: number;
  clock_in: string;
  clock_out: string | null;
  break_start: string | null;
  break_end: string | null;
  work_date: string;
  work_hours: number | null;
  overtime_hours: number | null;
  notes: string | null;
}

interface StaffAttendance {
  staff_id: number;
  display_name: string;
  role_level: number;
  attendance_id: number | null;
  clock_in: string | null;
  clock_out: string | null;
  break_start: string | null;
  break_end: string | null;
  work_hours: number | null;
  overtime_hours: number | null;
  shift_start: string | null;
  shift_end: string | null;
  is_holiday: boolean;
}

interface Schedule {
  id: number;
  staff_id: number;
  display_name: string;
  schedule_date: string;
  shift_start: string;
  shift_end: string;
  is_holiday: boolean;
  notes: string | null;
}

const AttendanceManagement: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAuth();
  const [activeTab, setActiveTab] = useState<'clock' | 'history' | 'shifts' | 'admin'>('clock');
  const [attendanceStatus, setAttendanceStatus] = useState<any>(null);
  const [history, setHistory] = useState<AttendanceRecord[]>([]);
  const [allStaffAttendance, setAllStaffAttendance] = useState<StaffAttendance[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [scheduleWeek, setScheduleWeek] = useState(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(monday.getDate() - monday.getDay() + 1);
    return monday.toISOString().split('T')[0];
  });

  const isAdmin = state.user?.roleLevel === 1;

  useEffect(() => {
    if (state.user) {
      fetchAttendanceStatus();
      fetchHistory();
      if (isAdmin) {
        fetchAllStaffAttendance();
      }
      fetchSchedules();
    }
  }, [state.user, selectedMonth, scheduleWeek]);

  const fetchAttendanceStatus = async () => {
    try {
      const response = await apiClient.get(`/attendance/status/${state.user?.id}`);
      setAttendanceStatus(response.data);
    } catch (error) {
      console.error('勤怠状態取得エラー:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const [year, month] = selectedMonth.split('-');
      const response = await apiClient.get(`/attendance/history/${state.user?.id}`, {
        params: { year, month }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('勤怠履歴取得エラー:', error);
    }
  };

  const fetchAllStaffAttendance = async () => {
    try {
      const response = await apiClient.get('/attendance/all/today');
      setAllStaffAttendance(response.data);
    } catch (error) {
      console.error('全スタッフ勤怠取得エラー:', error);
    }
  };

  const fetchSchedules = async () => {
    const endDate = new Date(scheduleWeek);
    endDate.setDate(endDate.getDate() + 6);
    
    try {
      const response = await apiClient.get('/attendance/schedule', {
        params: {
          start_date: scheduleWeek,
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      setSchedules(response.data);
    } catch (error) {
      console.error('シフト取得エラー:', error);
    }
  };

  const handleClockIn = async () => {
    setLoading(true);
    try {
      await apiClient.post('/attendance/clock-in', {
        staff_id: state.user?.id
      });
      alert('出勤打刻が完了しました');
      fetchAttendanceStatus();
      if (isAdmin) fetchAllStaffAttendance();
    } catch (error: any) {
      alert(error.response?.data?.error || '出勤打刻に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!confirm('退勤打刻をしますか？')) return;
    
    setLoading(true);
    try {
      await apiClient.post('/attendance/clock-out', {
        staff_id: state.user?.id
      });
      alert('退勤打刻が完了しました');
      fetchAttendanceStatus();
      fetchHistory();
      if (isAdmin) fetchAllStaffAttendance();
    } catch (error: any) {
      alert(error.response?.data?.error || '退勤打刻に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleBreakStart = async () => {
    setLoading(true);
    try {
      await apiClient.post('/attendance/break-start', {
        staff_id: state.user?.id
      });
      alert('休憩開始を記録しました');
      fetchAttendanceStatus();
    } catch (error: any) {
      alert(error.response?.data?.error || '休憩開始の記録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleBreakEnd = async () => {
    setLoading(true);
    try {
      await apiClient.post('/attendance/break-end', {
        staff_id: state.user?.id
      });
      alert('休憩終了を記録しました');
      fetchAttendanceStatus();
    } catch (error: any) {
      alert(error.response?.data?.error || '休憩終了の記録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (datetime: string | null) => {
    if (!datetime) return '-';
    return new Date(datetime).toLocaleTimeString('ja-JP', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ja-JP', {
      month: 'numeric',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const calculateTotalHours = () => {
    return history.reduce((sum, record) => sum + (record.work_hours || 0), 0).toFixed(1);
  };

  const calculateTotalOvertime = () => {
    return history.reduce((sum, record) => sum + (record.overtime_hours || 0), 0).toFixed(1);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Clock className="h-8 w-8 text-blue-600" />
          勤怠管理
        </h1>
        <button
          onClick={() => navigate('/home')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Home className="w-5 h-5" />
          ホーム
        </button>
      </div>

      {/* タブナビゲーション */}
      <div className="mb-6 border-b">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('clock')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'clock'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            打刻
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            勤怠履歴
          </button>
          <button
            onClick={() => setActiveTab('shifts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'shifts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            シフト表
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('admin')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'admin'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              管理者
            </button>
          )}
        </nav>
      </div>

      {/* 打刻タブ */}
      {activeTab === 'clock' && (
        <div className="space-y-6">
          {/* 現在の状態 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">現在の状態</h2>
            <div className="flex items-center gap-4 mb-6">
              <div className={`px-4 py-2 rounded-full font-semibold ${
                attendanceStatus?.status === 'clocked_in' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {attendanceStatus?.status === 'clocked_in' ? '出勤中' : '未出勤'}
              </div>
              {attendanceStatus?.attendance && (
                <div className="text-sm text-gray-600">
                  出勤: {formatTime(attendanceStatus.attendance.clock_in)}
                  {attendanceStatus.attendance.break_start && !attendanceStatus.attendance.break_end && (
                    <span className="ml-4 text-orange-600">休憩中</span>
                  )}
                </div>
              )}
            </div>

            {/* 打刻ボタン */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button
                onClick={handleClockIn}
                disabled={loading || attendanceStatus?.status === 'clocked_in'}
                className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                  attendanceStatus?.status === 'clocked_in'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <UserCheck className="h-8 w-8" />
                <span className="font-semibold">出勤</span>
              </button>

              <button
                onClick={handleClockOut}
                disabled={loading || attendanceStatus?.status !== 'clocked_in'}
                className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                  attendanceStatus?.status !== 'clocked_in'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                <LogOut className="h-8 w-8" />
                <span className="font-semibold">退勤</span>
              </button>

              <button
                onClick={handleBreakStart}
                disabled={
                  loading || 
                  attendanceStatus?.status !== 'clocked_in' ||
                  (attendanceStatus?.attendance?.break_start && !attendanceStatus?.attendance?.break_end)
                }
                className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                  attendanceStatus?.status !== 'clocked_in' ||
                  (attendanceStatus?.attendance?.break_start && !attendanceStatus?.attendance?.break_end)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                <Coffee className="h-8 w-8" />
                <span className="font-semibold">休憩開始</span>
              </button>

              <button
                onClick={handleBreakEnd}
                disabled={
                  loading || 
                  !attendanceStatus?.attendance?.break_start ||
                  attendanceStatus?.attendance?.break_end
                }
                className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                  !attendanceStatus?.attendance?.break_start ||
                  attendanceStatus?.attendance?.break_end
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                <UserCheck className="h-8 w-8" />
                <span className="font-semibold">休憩終了</span>
              </button>
            </div>
          </div>

          {/* 今日の勤務情報 */}
          {attendanceStatus?.attendance && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">今日の勤務</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">出勤時刻</p>
                  <p className="text-xl font-semibold">
                    {formatTime(attendanceStatus.attendance.clock_in)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">退勤時刻</p>
                  <p className="text-xl font-semibold">
                    {formatTime(attendanceStatus.attendance.clock_out)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">休憩時間</p>
                  <p className="text-xl font-semibold">
                    {attendanceStatus.attendance.break_hours 
                      ? `${attendanceStatus.attendance.break_hours}時間`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">勤務時間</p>
                  <p className="text-xl font-semibold">
                    {attendanceStatus.attendance.work_hours 
                      ? `${attendanceStatus.attendance.work_hours}時間`
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 勤怠履歴タブ */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* 月選択 */}
          <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border rounded-md"
            />
            <div className="flex gap-4">
              <div className="text-sm">
                <span className="text-gray-600">総勤務時間: </span>
                <span className="font-semibold text-lg">{calculateTotalHours()}時間</span>
              </div>
              <div className="text-sm">
                <span className="text-gray-600">総残業時間: </span>
                <span className="font-semibold text-lg text-orange-600">
                  {calculateTotalOvertime()}時間
                </span>
              </div>
            </div>
          </div>

          {/* 履歴テーブル */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    日付
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    出勤
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    退勤
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    休憩
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    勤務時間
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    残業
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map((record) => (
                  <tr key={record.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatDate(record.work_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTime(record.clock_in)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTime(record.clock_out)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.break_start && record.break_end
                        ? `${formatTime(record.break_start)} - ${formatTime(record.break_end)}`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.work_hours ? `${record.work_hours}時間` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {record.overtime_hours && record.overtime_hours > 0 ? (
                        <span className="text-orange-600 font-semibold">
                          {record.overtime_hours}時間
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* シフト表タブ */}
      {activeTab === 'shifts' && (
        <ShiftManagement />
      )}

      {/* 管理者タブ */}
      {activeTab === 'admin' && isAdmin && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              本日の勤怠状況
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      スタッフ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状態
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      出勤
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      退勤
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      勤務時間
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      シフト
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allStaffAttendance.map((staff) => (
                    <tr key={staff.staff_id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {staff.display_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {staff.is_holiday ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                            休日
                          </span>
                        ) : staff.clock_in && !staff.clock_out ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            出勤中
                          </span>
                        ) : staff.clock_out ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                            退勤済
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                            未出勤
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTime(staff.clock_in)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTime(staff.clock_out)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {staff.work_hours ? `${staff.work_hours}時間` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {staff.shift_start && staff.shift_end
                          ? `${staff.shift_start} - ${staff.shift_end}`
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* アラート */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  遅刻や欠勤がある場合は、こちらに表示されます
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceManagement;